<template>
  <div>
    <slot
      v-if="state.ERROR"
      name="error"
      :error="error"
    >
      <ProfileVideosEmptyState
        :options="{
          icon: 'md:block',
          title: error === 'error' ? $t('fallback.unknown.title') : error,
          message: error === 'error' ? $t('fallback.unknown.message') : '',
        }"
      />
    </slot>

    <!-- START: Actual content area -->
    <slot
      v-if="!state.EMPTY"
      :response="response"
      :content="content"
    >
      <CardRow
        :items="content"
        :transformer="cardRowOptions.transformer"
        :actions="cardRowOptions.actions"
        item-type="video"
        class="justify-content-left"
      />
    </slot>

    <slot
      v-if="state.EMPTY && !state.LOADING && !state.ERROR"
      name="empty"
    >
      <ProfileVideosEmptyState />
    </slot>
    <!-- END: Actual content area -->

    <slot
      v-if="state.LOADING"
      name="loading"
    >
      <div
        ref="seeMoreElement"
        class="
          loader__see-more
          row justify-content-center align-items-center
        "
      >
        <BrowseLoading :loading="true" />
      </div>
    </slot>
    <div
      v-if="hasMorePages"
      class="text-center"
    >
      <VsButton
        :loading="state.LOADING"
        class="btn--outline--dark"
        @click="profileVideoLoadingMixin_runLoadVideoSequence"
      >
        Load more
      </VsButton>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType, VueConstructor } from 'vue';
import isFunction from 'lodash/isFunction';
import { Model, Helper } from '@viddsee/server-api-client';

import { Nullable } from '@/scripts/models/utility-types';
import { ContentLoaderState } from './ContentLoaderState';

import BrowseLoading from '@/scripts/Components/Molecules/BrowseLoading.vue';
import CardRow from '@/scripts/Components/Row/CardRow.vue';
import VsButton from '@/scripts/Components/Molecules/VsButton.vue';

import ProfileVideosEmptyState from '../ProfileVideosEmptyState.vue';
import { ProfileVideoLoadingMixin } from '../../mixins/profileVideoLoadingMixinTypes';
import profileVideoLoadingMixin from '../../mixins/profileVideoLoadingMixin';
import WaypointHandler from '@/scripts/Components/libs/waypoint-handler';

type ContentLoaderResponseType = Omit<Model.Pagination.PaginatedItemsResponse<any>, 'order'>
interface ComponentData {
  response: ContentLoaderResponseType;
  loading: boolean;
  waypoint: Nullable<WaypointHandler>;
  error: string | false;
}

const getInitialPagination = (): ContentLoaderResponseType => ({
  page: -1,
  perPage: 10,
  pageCount: 0,
  itemCount: 0,
  items: [],
});

export default (Vue as VueConstructor<
  Vue & ProfileVideoLoadingMixin<ContentLoaderResponseType>
>).extend({
  components: {
    VsButton,
    BrowseLoading,
    CardRow,
    ProfileVideosEmptyState,
  },
  mixins: [
    profileVideoLoadingMixin,
  ],
  props: {
    loadFn: {
      type: Function,
      required: true,
    },

    processContentFn: {
      type: [Function, Boolean],
      default(result: ContentLoaderResponseType): ContentLoaderResponseType {
        result.items = Helper.convertToCamelCase(result.items);
        return result;
      },
    },

    /**
     * Parameters needed to be passe down the ProfileVideos component
     */
    profileOwner: {
      type: Object as PropType<any>,
      required: true,
    },

    /**
     * additional information that get passed down to cardRow
     * See cardRow component's prop
     */
    cardRowOptions: {
      type: Object as PropType<any>,
      default: (): {} => ({}),
    },
  },
  data(): ComponentData {
    return {
      response: getInitialPagination(),
      loading: true,
      waypoint: null,

      // State
      error: false,
    };
  },
  computed: {
    content(): any[] {
      return this.response.items;
    },
    hasMorePages(): boolean {
      if (this.response.page < 0) {
        // Page is not ready, return false
        return false;
      }
      return this.response.page < (this.response.pageCount - 1);
    },
    state(): ContentLoaderState {
      return {
        ERROR: !!this.error,
        LOADING: !!this.loading,
        EMPTY: this.response.items.length <= 0,
      };
    },
  },
  mounted(): void {
    this.profileVideoLoadingMixin_runLoadVideoSequence();
  },
  activated(): void {
    if (this.response.page > -1) {
      this.profileVideoLoadingMixin_refreshLoadVideoSequence();
    }
  },
  methods: {
    addContent(content: any, shouldAppendToBack = false): void {
      this.response.items.unshift(content);
    },
    removeContent(content: any): void {
      const keysToMatch = Object.keys(content);
      this.response.items = this.response.items
        .filter((localContent) => {
          const match = keysToMatch.every(key => localContent[key] === content[key]);
          return !match;
        });
    },
    updateContent(content: any): void {
      const keysToMatch = Object.keys(content);
      this.response.items = this.response.items
        .map((localContent) => {
          const match = keysToMatch.every(key => localContent[key] === content[key]);
          return match ? content : localContent;
        });
    },

    processContentPassThrough(result: ContentLoaderResponseType): ContentLoaderResponseType { return result; },

    /**
     * Override loadVideos function for playlists
     */
    // eslint-disable-next-line @typescript-eslint/camelcase
    profileVideoLoadingMixin_loadVideos(): Promise<Model.Pagination.PaginatedItemsResponse<any>> {
      return this.loadFn({
        params: {
          perPage: this.response.perPage,
          page: this.response.page + 1,
          id: this.profileOwner.profile.vanity,
        },
      });
    },

    /**
     * Override processVideos function for passthrough
     */
    // eslint-disable-next-line @typescript-eslint/camelcase
    profileVideoLoadingMixin_processVideos(result: ContentLoaderResponseType): ContentLoaderResponseType {
      if (this.processContentFn === false || !isFunction(this.processContentFn)) {
        return this.processContentPassThrough(result);
      }
      return this.processContentFn(result);
    },

    // Content loader methods
    // eslint-disable-next-line @typescript-eslint/camelcase
    $_resetResponse(): void {
      this.response = getInitialPagination();
    },
  },
});
</script>

<style style="scss" scoped>
.loader__see-more {
  margin-top: 20vh;
  margin-bottom: 120px;
}
</style>
